import React, { useState } from "react";
import { FiEye, FiFilter, FiX } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

const TransactionLogs = ({ data, onFilterChange, filters }) => {
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);
  const [localFilters, setLocalFilters] = useState({
    user_type: filters?.user_type || "",
    txtype: filters?.txtype || "",
    amount_min: filters?.amount_min || "",
    amount_max: filters?.amount_max || "",
    search: filters?.search || "",
    user_id: filters?.user_id || "",
    order_id: filters?.order_id || "",
    payment_id: filters?.payment_id || "",
  });

  // Update local filters when props change (e.g., from URL parameters)
  React.useEffect(() => {
    setLocalFilters({
      user_type: filters?.user_type || "",
      txtype: filters?.txtype || "",
      amount_min: filters?.amount_min || "",
      amount_max: filters?.amount_max || "",
      search: filters?.search || "",
      user_id: filters?.user_id || "",
      order_id: filters?.order_id || "",
      payment_id: filters?.payment_id || "",
    });
    
    // Auto-show filters if any filter is pre-filled from URL
    const hasPrefilledFilters = Object.values(filters || {}).some(value =>
      value && value !== "" && value !== 1 && value !== 30 // Exclude default page and per_page values
    );
    if (hasPrefilledFilters) {
      setShowFilters(true);
    }
  }, [filters]);

  const transactionTypes = [
    { value: "claim_payment", label: "Bayi Dekont Girildi" },
    { value: "subreseller_claim_payment", label: "Alt Bayi Dekont Girildi" },
    { value: "reseller_transfer", label: "Bayi Elmas Satın Alma" },
    { value: "retail_order", label: "Bot Yükleme" },
    { value: "subreseller_retail_order", label: "Alt Bayi Bot Yükleme" },
    { value: "payout", label: "Ödeme Talebi" },
    { value: "revert", label: "İptal/İade" },
    { value: "misc", label: "Diğer" },
  ];

  const userTypes = [
    { value: "reseller", label: "Bayi" },
    { value: "subreseller", label: "Alt Bayi" },
  ];

  const handleFilterChange = (key, value) => {
    setLocalFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    onFilterChange(localFilters);
    setShowFilters(false);
  };

  const clearFilters = () => {
    const emptyFilters = {
      user_type: "",
      txtype: "",
      amount_min: "",
      amount_max: "",
      search: "",
      user_id: "",
      order_id: "",
      payment_id: "",
    };
    setLocalFilters(emptyFilters);
    onFilterChange(emptyFilters);
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('tr-TR');
  };

  const getTransactionTypeLabel = (txtype) => {
    const type = transactionTypes.find(t => t.value === txtype);
    return type ? type.label : txtype;
  };

  const handleViewDetails = (logId) => {
    navigate(`/admin/transaction-logs/detail/${logId}`);
  };

  return (
    <div className="overflow-x-auto p-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
        <h3 className="text-xl font-semibold text-white-100">İşlem Logları</h3>
        <div className="flex gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="bg-primary text-white px-4 py-2 rounded-md font-semibold flex items-center gap-2"
          >
            <FiFilter size={16} />
            Filtrele
          </button>
          {Object.values(localFilters).some(v => v) && (
            <button
              onClick={clearFilters}
              className="bg-red-500 text-white px-4 py-2 rounded-md font-semibold flex items-center gap-2"
            >
              <FiX size={16} />
              Temizle
            </button>
          )}
        </div>
      </div>

      {showFilters && (
        <div className="bg-gray-800 p-4 mb-4 rounded-md">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <div>
              <label className="block text-white-100 font-medium mb-1">Kullanıcı Tipi</label>
              <select
                value={localFilters.user_type}
                onChange={(e) => handleFilterChange('user_type', e.target.value)}
                className="w-full p-2 rounded-md bg-gray-700 text-white"
              >
                <option value="">Tüm Tipler</option>
                {userTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-white-100 font-medium mb-1">İşlem Tipi</label>
              <select
                value={localFilters.txtype}
                onChange={(e) => handleFilterChange('txtype', e.target.value)}
                className="w-full p-2 rounded-md bg-gray-700 text-white"
              >
                <option value="">Tüm Tipler</option>
                {transactionTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-white-100 font-medium mb-1">Min Tutar (TL)</label>
              <input
                type="number"
                value={localFilters.amount_min}
                onChange={(e) => handleFilterChange('amount_min', e.target.value)}
                className="w-full p-2 rounded-md bg-gray-700 text-white"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-white-100 font-medium mb-1">Max Tutar (TL)</label>
              <input
                type="number"
                value={localFilters.amount_max}
                onChange={(e) => handleFilterChange('amount_max', e.target.value)}
                className="w-full p-2 rounded-md bg-gray-700 text-white"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-white-100 font-medium mb-1">Kullanıcı ID</label>
              <input
                type="text"
                value={localFilters.user_id}
                onChange={(e) => handleFilterChange('user_id', e.target.value)}
                className="w-full p-2 rounded-md bg-gray-700 text-white"
                placeholder="Kullanıcı UUID"
              />
            </div>

            <div>
              <label className="block text-white-100 font-medium mb-1">Sipariş ID</label>
              <input
                type="text"
                value={localFilters.order_id}
                onChange={(e) => handleFilterChange('order_id', e.target.value)}
                className="w-full p-2 rounded-md bg-gray-700 text-white"
                placeholder="Sipariş UUID"
              />
            </div>

            <div>
              <label className="block text-white-100 font-medium mb-1">Ödeme ID</label>
              <input
                type="text"
                value={localFilters.payment_id}
                onChange={(e) => handleFilterChange('payment_id', e.target.value)}
                className="w-full p-2 rounded-md bg-gray-700 text-white"
                placeholder="Ödeme UUID"
              />
            </div>

            <div>
              <label className="block text-white-100 font-medium mb-1">Arama</label>
              <input
                type="text"
                value={localFilters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full p-2 rounded-md bg-gray-700 text-white"
                placeholder="Mesaj, RefID, Kullanıcı adı"
              />
            </div>
          </div>
          
          <div className="flex gap-2 mt-4">
            <button
              onClick={applyFilters}
              className="bg-green-600 text-white px-4 py-2 rounded-md font-semibold"
            >
              Filtreleri Uygula
            </button>
            <button
              onClick={() => setShowFilters(false)}
              className="bg-gray-600 text-white px-4 py-2 rounded-md font-semibold"
            >
              İptal
            </button>
          </div>
        </div>
      )}

      {/* Mobile Card View */}
      <div className="block md:hidden space-y-4">
        {data?.map((log) => (
          <div key={log.id} className="bg-gray-800 p-4 rounded-md">
            <div className="flex justify-between items-start mb-2">
              <div className="text-sm text-gray-400">
                {formatDate(log.created_at)}
              </div>
              <button
                onClick={() => handleViewDetails(log.id)}
                className="bg-primary text-white px-2 py-1 rounded text-xs flex items-center gap-1"
              >
                <FiEye size={12} />
                Görüntüle
              </button>
            </div>
            
            <div className="space-y-2">
              <div>
                <span className="text-gray-400 text-sm">Kullanıcı:</span>
                <span className="text-white-100 ml-2">{log.user_data?.name || 'N/A'}</span>
                <span className="text-gray-400 ml-2">({log.user_data?.type == 'reseller' ? 'Bayi' : (log.user_data?.type == 'subreseller' ? 'Alt Bayi' : log.user_data?.type)})</span>
              </div>
              
              <div>
                <span className="text-gray-400 text-sm">Tip:</span>
                <span className="text-white-100 ml-2">{getTransactionTypeLabel(log.txtype)}</span>
              </div>
              
              <div>
                <span className="text-gray-400 text-sm">Tutar:</span>
                <span className="text-white-100 ml-2">
                  {log.amount_diamonds && `${log.amount_diamonds} 💎`}
                  {log.amount_diamonds && log.amount_try && ' / '}
                  {log.amount_try && `₺${formatAmount(log.amount_try)}`}
                </span>
              </div>
              
              {log.message && (
                <div>
                  <span className="text-gray-400 text-sm">Mesaj:</span>
                  <div className="text-white-100 text-sm mt-1 break-words">{log.message}</div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block">
        <table className="min-w-full divide-y-2 divide-gray-200 text-sm">
          <thead>
            <tr>
              <th className="px-4 py-2 text-left text-white-100">Tarih</th>
              <th className="px-4 py-2 text-left text-white-100">Kullanıcı</th>
              <th className="px-4 py-2 text-left text-white-100">Tip</th>
              <th className="px-4 py-2 text-left text-white-100">Tutar</th>
              <th className="px-4 py-2 text-left text-white-100">Bakiye Değişimi</th>
              <th className="px-4 py-2 text-left text-white-100">Mesaj</th>
              <th className="px-4 py-2 text-left text-white-100">İşlemler</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data?.map((log) => (
              <tr key={log.id} className="hover:bg-gray-800">
                <td className="px-4 py-2 text-white-100 text-xs">
                  {formatDate(log.created_at)}
                </td>
                <td className="px-4 py-2 text-white-100">
                  <div>
                    <div className="font-medium">{log.user_data?.name || 'N/A'}</div>
                    <div className="text-xs text-gray-400">{log.user_data?.type == 'reseller' ? 'Bayi' : (log.user_data?.type == 'subreseller' ? 'Alt Bayi' : log.user_data?.type)}</div>
                  </div>
                </td>
                <td className="px-4 py-2 text-white-100 text-xs">
                  {getTransactionTypeLabel(log.txtype)}
                </td>
                <td className="px-4 py-2 text-white-100">
                  <div>
                    {log.amount_diamonds && <div>{log.amount_diamonds} 💎</div>}
                    {log.amount_try && <div>₺{formatAmount(log.amount_try)}</div>}
                  </div>
                </td>
                <td className="px-4 py-2 text-white-100">
                  <div className="text-xs">
                    <div>Önce: ₺{formatAmount(log.balance_before)}</div>
                    <div>Sonra: ₺{formatAmount(log.balance_after)}</div>
                  </div>
                </td>
                <td className="px-4 py-2 text-white-100 max-w-xs">
                  <div className="truncate text-xs" title={log.message}>
                    {log.message || '-'}
                  </div>
                </td>
                <td className="px-4 py-2 text-white-100">
                  <button
                    onClick={() => handleViewDetails(log.id)}
                    className="bg-primary text-white rounded-md px-2 py-1 flex items-center font-semibold text-xs"
                  >
                    <FiEye className="mr-1" size={12} />
                    Görüntüle
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(!data || data.length === 0) && (
        <div className="text-center py-8 text-gray-400">
          İşlem logu bulunamadı.
        </div>
      )}
    </div>
  );
};

export default TransactionLogs;